/**
 * Field Registry — Single Source of Truth for all LabelData fields
 *
 * Each field definition contains EVERYTHING needed to:
 *   1. Detect what field the AI was asking about (askPatterns)
 *   2. Map a user answer to the correct field (via applyFieldUpdate)
 *   3. Parse AI [FIELD:...] markers (aiMarkerKeys)
 *   4. Build the system prompt rationale (rationale)
 *   5. Know when a field is required (required)
 *   6. Know the collection order (order)
 *
 * To add a new field:
 *   1. Add it to LabelData interface + defaultLabelData in LabelGeneratorStore.ts
 *   2. Add one entry here
 *   3. Render it in LabelGenerator.tsx
 *   Done — all detection, update, and prompt logic is automatic.
 */

import type { LabelData } from './LabelGeneratorStore'

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

export interface FieldDefinition {
  /** Human-readable label shown in prompts and errors */
  label: string

  /**
   * Regex patterns that match the AI's question for this field.
   * Matched against the last assistant message (last 1-3 sentences).
   * ORDER WITHIN EACH ENTRY MATTERS — first match wins across the registry.
   */
  askPatterns: RegExp[]

  /**
   * Alternative field names the AI may use in [FIELD:x=y] markers.
   * The primary key is the LabelData field name itself.
   */
  aiMarkerAliases?: string[]

  /**
   * One-sentence rationale explaining WHY this field is needed.
   * Injected into the system prompt so the AI always gives a reason when asking.
   */
  rationale: string

  /**
   * If provided, the user's answer is checked against these phrases.
   * When matched, skipAction is applied instead of setting the field value.
   */
  skipPhrases?: RegExp

  /**
   * What to apply to LabelData when skipPhrases match.
   * Usually sets a boolean flag like euImporterSkipped.
   */
  skipAction?: Partial<LabelData>

  /**
   * Extra fields to set when this field is answered.
   * Called with (value, currentData) → additional Partial<LabelData> to merge.
   * Use for cross-population like countryOfOrigin ↔ manufacturerCountry.
   */
  crossPopulate?: (value: string, currentData: LabelData) => Partial<LabelData>

  /**
   * Whether this field should be collected for the given label data state.
   * Used by doAIHandoff to build neededFields and doNotAsk lists.
   */
  required: (data: LabelData) => boolean

  /**
   * Collection order — lower numbers are asked first.
   * EU/UK rep fields use higher numbers (collected after core fields).
   */
  order: number

  /**
   * If true, this field is NEVER emitted in the AI [FIELD:...] parsing path.
   * Used for fields like manufacturerCountry that we set via crossPopulate only.
   */
  derivedOnly?: boolean

  /**
   * Describes how to ask for this field — used to build the system prompt.
   * If omitted, the AI asks for it with just the label and rationale.
   */
  collectInstructions?: string
}

// ─────────────────────────────────────────────────────────────
// Helpers (imported at use-site, defined here for use in registry)
// ─────────────────────────────────────────────────────────────

export function hasEUMarket(data: LabelData): boolean {
  return data.targetMarkets.some((m) => ['EU', 'DE', 'FR', 'IT', 'ES', 'NL', 'PL', 'BE', 'AT', 'SE', 'DK', 'FI', 'PT', 'IE', 'CZ', 'HU', 'RO', 'BG', 'HR', 'SK', 'SI', 'EE', 'LV', 'LT', 'LU', 'CY', 'MT', 'GR'].includes(m))
}

export function isCERegulated(data: LabelData): boolean {
  return data.productCategory === 'electronics' || data.productType.startsWith('electronics') || data.productCategory === 'toy'
}

// Matches both ISO 2-letter codes AND full country names (case-insensitive).
// Must stay in sync with LabelGeneratorStore.ts EU_COUNTRIES list.
const EU_COUNTRY_LIST = [
  // ISO codes
  'at', 'be', 'bg', 'hr', 'cy', 'cz', 'dk', 'ee', 'fi', 'fr', 'de', 'gr',
  'hu', 'ie', 'it', 'lv', 'lt', 'lu', 'mt', 'nl', 'pl', 'pt', 'ro', 'sk',
  'si', 'es', 'se',
  // Full names
  'austria', 'belgium', 'bulgaria', 'croatia', 'cyprus', 'czech republic',
  'czechia', 'denmark', 'estonia', 'finland', 'france', 'germany', 'greece',
  'hungary', 'ireland', 'italy', 'latvia', 'lithuania', 'luxembourg', 'malta',
  'netherlands', 'holland', 'poland', 'portugal', 'romania', 'slovakia',
  'slovenia', 'spain', 'sweden',
]

export function isEUCountry(country: string): boolean {
  const c = country.toLowerCase().trim()
  return EU_COUNTRY_LIST.includes(c)
}

export function isUKCARegulated(data: LabelData): boolean {
  return isCERegulated(data)
}

const UK_COUNTRY_NAMES = ['uk', 'gb', 'united kingdom', 'great britain', 'england', 'scotland', 'wales', 'northern ireland']

export function isUKCountry(country: string): boolean {
  return UK_COUNTRY_NAMES.includes(country.toLowerCase().trim())
}

export function shouldRequireECRep(data: LabelData): boolean {
  return hasEUMarket(data) && isCERegulated(data) && !isEUCountry(data.manufacturerCountry)
}

export function shouldRequireEUImporter(data: LabelData): boolean {
  return hasEUMarket(data) && !isCERegulated(data) && !isEUCountry(data.manufacturerCountry)
}

export function shouldRequireUKRep(data: LabelData): boolean {
  // Required ONLY when ALL three conditions are met:
  // 1. UK is a target market
  // 2. Manufacturer is NOT based in the UK
  // 3. Product is UKCA-regulated (electronics, toys, medical, PPE)
  // At handoff, manufacturerCountry is not yet known — we assume non-UK until proven otherwise.
  const manufacturerIsUK = data.manufacturerCountry ? isUKCountry(data.manufacturerCountry) : false
  return data.targetMarkets.includes('UK') && !manufacturerIsUK && isUKCARegulated(data)
}

// ─────────────────────────────────────────────────────────────
// The Registry
// ─────────────────────────────────────────────────────────────

// Matches explicit skip/decline responses for EU importer question.
// "have it" and "have this" removed — they indicate the user WANTS to provide the info.
// "no" only matches as a standalone word to avoid catching "no, the importer is ABC".
// "later" only in skip context ("add it later", "skip for now") not "I'll send it later today".
const SKIP_IMPORTER = /^(skip|don'?t have|do not have|not now|n\/a|none)$|^\s*no[.,!]?\s*$|\b(don'?t have it|do not have it|will add later|add.*later|skip for now|no eu importer|don'?t need|not applicable)\b/i

export const FIELD_REGISTRY: Record<string, FieldDefinition> = {

  // ── Core product fields ──────────────────────────────────────

  productName: {
    label: 'Product Name',
    order: 10,
    askPatterns: [
      /official.*product name/i,
      /what is the product name/i,
      /product name/i,
    ],
    rationale: 'The product name is required on all compliance labels in all major markets.',
    required: () => true,
  },

  modelRef: {
    label: 'Model / SKU',
    order: 20,
    askPatterns: [
      /product identifier/i,
      /model.*(?:ref|number|sku)/i,
      /\bsku\b/i,
      /batch.*serial/i,
      /model\/sku/i,
      /sku.*model/i,
      /traceability.*(?:recall|compliance)/i,
    ],
    aiMarkerAliases: ['productIdentifier'],
    rationale: 'A model or SKU is required for product traceability and recall compliance.',
    required: () => true,
  },

  material: {
    label: 'Primary Material',
    order: 30,
    askPatterns: [
      /primary materials?/i,
      /what.*material/i,
      /made (?:of|from)/i,
      /provide.*material/i,
    ],
    rationale: 'Primary materials determine which recycling symbols and environmental compliance marks apply to your label.',
    required: () => true,
  },

  // ── Manufacturer fields ──────────────────────────────────────
  // NOTE: manufacturerAddress MUST be listed before manufacturerName
  // in askPatterns priority — address question must not match name patterns.

  manufacturerAddress: {
    label: 'Manufacturer Address',
    order: 50,
    askPatterns: [
      /manufacturer'?s?\s+(?:full\s+)?(?:physical\s+)?address/i,
      /physical address/i,
      /manufactur.*address/i,
      /address of.*manufactur/i,
    ],
    rationale: 'A physical manufacturer address is required for consumer safety regulations and product traceability.',
    required: () => true,
  },

  manufacturerName: {
    label: 'Manufacturer Name',
    order: 40,
    askPatterns: [
      /manufacturer'?s?\s+(?:full\s+)?legal name/i,
      /legal name of.*manufactur/i,
      /provide.*manufacturer'?s?\s+(?:full\s+)?(?:legal\s+)?name/i,
      /manufactur.*legal name/i,
      /(?:provide|what is|could you.*provide|please.*provide).*manufactur.*name/i,
      /name of the manufactur/i,
      /manufactur.*(?:company|brand|firm)\s*name/i,
      /(?:company|brand|firm)\s*name.*manufactur/i,
      /(?:what is|provide).*(?:the\s+)?manufactur(?:er|ing\s+company)/i,
    ],
    rationale: "Regulations in all major markets (US, EU, UK, Canada) require the manufacturer's legal name on the label.",
    required: () => true,
  },

  countryOfOrigin: {
    label: 'Country of Origin',
    order: 60,
    askPatterns: [
      /country of origin/i,
      /where.*(?:is.*)?(?:the\s+)?product.*(?:made|manufactured|produced)/i,
      /where.*(?:is.*)?(?:the\s+)?(?:toy|item|product|it).*(?:made|manufactured|produced)/i,
      /where.*produced/i,
      /where.*manufactured/i,
      /(?:made|manufactured|produced)\s+in\b/i,
      /\bmade in\b/i,
    ],
    rationale: 'Must appear as "MADE IN [COUNTRY]" — legally required for customs and trade compliance in most markets.',
    crossPopulate: (value, currentData) =>
      !currentData.manufacturerCountry ? { manufacturerCountry: value } : {},
    required: () => true,
  },

  manufacturerCountry: {
    label: "Manufacturer's Country",
    order: 61,
    askPatterns: [
      /manufacturer'?s?\s+country/i,
      /where.*(?:is.*)?(?:the\s+)?manufacturer.*(?:based|located)/i,
      /country.*(?:where.*)?manufacturer/i,
      /which country.*manufacturer/i,
    ],
    rationale: "Needed to determine if an EU REP, EU Importer, or UK Responsible Person is required.",
    crossPopulate: (value, currentData) =>
      !currentData.countryOfOrigin ? { countryOfOrigin: value } : {},
    // derivedOnly: we prefer to set this via crossPopulate from countryOfOrigin,
    // but we still accept it directly if the AI asks for it separately.
    required: (data) => hasEUMarket(data) || data.targetMarkets.includes('UK'),
  },

  safetyWarning: {
    label: 'Safety Warning',
    order: 70,
    askPatterns: [
      /safety warning/i,
      /warning text/i,
      /provide.*warning/i,
    ],
    rationale: 'Safety warnings may be required for products with hazards (e.g. small parts, chemicals, electrical).',
    required: () => false, // optional — only if user provides one
  },

  // ── Regulatory IDs ──────────────────────────────────────────

  fccId: {
    label: 'FCC ID',
    order: 75,
    askPatterns: [
      /\bfcc\s*id\b/i,
      /fcc identifier/i,
    ],
    rationale: 'FCC ID is required for wireless/radio devices sold in the US (FCC Part 15).',
    required: (data) => data.targetMarkets.includes('US') && data.radio === true,
  },

  icId: {
    label: 'IC ID',
    order: 76,
    askPatterns: [
      /\bic\s*id\b/i,
      /industry canada/i,
    ],
    rationale: 'IC ID is required for wireless/radio devices sold in Canada (ISED regulations).',
    required: (data) => data.targetMarkets.includes('CA') && data.radio === true,
  },

  upcCode: {
    label: 'UPC Barcode',
    order: 77,
    askPatterns: [
      /\bupc\b/i,
      /barcode/i,
      /upc.*code/i,
    ],
    rationale: 'A UPC barcode is optional but commonly needed for retail distribution and point-of-sale scanning.',
    required: () => false,
  },

  // ── EU Representative ────────────────────────────────────────
  // NOTE: ecRepAddress MUST be listed before ecRepName in the registry
  // (earlier order in askPatterns priority).

  ecRepAddress: {
    label: 'EU REP Address',
    order: 82,
    askPatterns: [
      /eu\s*rep.*address/i,
      /representative.*address/i,
      /authori[sz]ed.*representative.*address/i,
    ],
    rationale: 'Complete physical EU address is required for the EU Authorised Representative mandate.',
    required: shouldRequireECRep,
    collectInstructions: 'Ask for complete physical EU address (street+number, postal code, city, EU country — NO PO Boxes) → [FIELD:ecRepAddress=...]',
  },

  ecRepPhone: {
    label: 'EU REP Phone',
    order: 83,
    // Phone patterns come BEFORE ecRepEmail in DETECTION_ORDER (order 83 < 84).
    // This ensures "contact phone number" maps to ecRepPhone, not ecRepEmail.
    askPatterns: [
      /eu\s*rep.*phone/i,
      /representative.*phone/i,
      /contact.*phone/i,
      /phone.*(?:eu\s*rep|representative)/i,
      /provide.*phone/i,
    ],
    rationale: 'At least one contact (email or phone) is required for the EU REP for market surveillance authorities.',
    required: (data) => shouldRequireECRep(data) && !data.ecRepEmail,
  },

  ecRepEmail: {
    label: 'EU REP Email',
    order: 84,
    askPatterns: [
      /eu\s*rep.*(?:email|contact)/i,
      /representative.*email/i,
      /contact.*email/i,
      /provide.*(?:contact\s+)?email/i,
      // Generic "contact" question — falls here if phone patterns didn't match
      /provide.*contact.*(?:email|phone)/i,
    ],
    rationale: 'At least one contact (email or phone) is required for the EU REP for market surveillance authorities.',
    required: (data) => shouldRequireECRep(data) && !data.ecRepPhone,
  },

  ecRepName: {
    label: 'EU REP Company Name',
    order: 81,
    askPatterns: [
      /eu\s*rep.*(?:company|legal)\s*name/i,
      /eu.*authori[sz]ed.*representative/i,
      /full legal.*(?:company\s+)?name.*(?:eu\s*rep|representative)/i,
      /representative.*(?:company|legal)\s*name/i,
    ],
    rationale: 'The full legal company name of the EU Authorised Representative as it appears on the Mandate Agreement.',
    required: shouldRequireECRep,
    collectInstructions: `Collect EU REP fields in this order:
  1. Full legal company name → [FIELD:ecRepName=...]
  2. Complete physical EU address (street+number, postal code, city, country — NO PO Boxes) → [FIELD:ecRepAddress=...]
  3. Contact: email → [FIELD:ecRepEmail=...] or phone → [FIELD:ecRepPhone=...] (at least one required)
  If user provides all in one message (Company:/Address:/Contact: format), emit all markers at once.`,
  },

  // ── EU Importer ──────────────────────────────────────────────
  // NOTE: euImporterAddress MUST be listed before euImporterName.

  euImporterAddress: {
    label: 'EU Importer Address',
    order: 92,
    askPatterns: [
      /eu\s*importer.*address/i,
      /importer.*address/i,
      /importer.*(?:full|physical|postal|complete).*address/i,
      /address.*eu\s*importer/i,
    ],
    rationale: 'Complete physical EU address of the EU Importer is required for the "Imported by:" label marking.',
    skipPhrases: SKIP_IMPORTER,
    skipAction: { euImporterSkipped: true },
    required: (data) => shouldRequireEUImporter(data) && !data.euImporterSkipped,
  },

  euImporterName: {
    label: 'EU Importer Name',
    order: 91,
    askPatterns: [
      /eu\s*importer.*name/i,
      /importer.*(?:legal\s+)?(?:company\s+)?name/i,
      /\beu importer\b/i,
      /(?:have|provide).*eu\s*importer/i,
      /importer.*information/i,
    ],
    rationale: 'Non-EU manufacturers selling non-CE products in the EU must have an EU Importer. Label shows "Imported by:" prefix per EU 2019/1020.',
    skipPhrases: SKIP_IMPORTER,
    skipAction: { euImporterSkipped: true },
    required: (data) => shouldRequireEUImporter(data) && !data.euImporterSkipped,
    collectInstructions: `Collect EU Importer fields in this order:
  1. First ask: "Do you have the EU Importer information? If not, you can skip it — the importer details can also be printed on a separate label or inside the packaging before shipment."
     - If user skips → emit [FIELD:euImporterSkipped=true] and move on.
     - If user confirms they have it → continue:
  2. Full legal company name → [FIELD:euImporterName=...]
  3. Complete physical EU address (street+number, postal code, city, EU country — NO PO Boxes) → [FIELD:euImporterAddress=...]`,
  },

  // ── UK Responsible Person ─────────────────────────────────────
  // NOTE: ukRepAddress MUST be listed before ukRepName.

  ukRepAddress: {
    label: 'UK Responsible Person Address',
    order: 102,
    askPatterns: [
      /uk\s*rep.*address/i,
      /responsible.*person.*address/i,
    ],
    rationale: 'Complete UK address of the UK Responsible Person is required for UKCA-regulated products sold in Great Britain.',
    required: shouldRequireUKRep,
  },

  ukRepName: {
    label: 'UK Responsible Person Name',
    order: 101,
    askPatterns: [
      /uk\s*rep/i,
      /uk.*responsible.*person/i,
    ],
    rationale: 'Non-UK manufacturers of UKCA-regulated products (electronics, toys, medical, PPE) selling in Great Britain must appoint a UK Responsible Person.',
    required: shouldRequireUKRep,
    collectInstructions: `Collect UK REP fields in this order:
  1. Full legal company name → [FIELD:ukRepName=...]
  2. Complete UK address → [FIELD:ukRepAddress=...]`,
  },
}

// ─────────────────────────────────────────────────────────────
// Registry-driven functions (replaces scattered logic)
// ─────────────────────────────────────────────────────────────

/**
 * Detect which field the AI was asking about from its message.
 * Scans last 1-3 sentences of the message.
 * Registry order determines priority (address before name, modelRef before productName, etc.)
 *
 * ORDERING RULES (enforced by object key order + explicit priority array):
 * - manufacturerAddress before manufacturerName
 * - modelRef before productName
 * - ecRepAddress before ecRepName
 * - euImporterAddress before euImporterName
 * - ukRepAddress before ukRepName
 * - countryOfOrigin / manufacturerCountry BEFORE manufacturerAddress
 *   (a "Made in China" answer must never be caught by the address pattern)
 * - manufacturerAddress before manufacturerName (within the address/name group)
 */
const DETECTION_ORDER: (keyof typeof FIELD_REGISTRY)[] = [
  'modelRef',
  'productName',
  'material',
  // Country fields FIRST — a country answer must never fall through to manufacturerAddress
  'countryOfOrigin',
  'manufacturerCountry',
  // Then manufacturer address / name
  'manufacturerAddress',   // address before name
  'manufacturerName',
  'fccId',
  'icId',
  'ecRepAddress',          // address before name
  'ecRepPhone',
  'ecRepEmail',
  'ecRepName',
  'euImporterAddress',     // address before name
  'euImporterName',
  'ukRepAddress',          // address before name
  'ukRepName',
  'safetyWarning',
  'upcCode',
]

export function detectAskedField(assistantMsg: string): keyof LabelData | null {
  if (!assistantMsg) return null

  const sentences = assistantMsg
    .split(/(?<=[.?!])\s+/)
    .map((s) => s.trim())
    .filter(Boolean)

  // Try matching from just the last sentence, expanding to last 3
  for (let take = 1; take <= Math.min(3, sentences.length); take++) {
    const fragment = sentences.slice(-take).join(' ')
    for (const fieldKey of DETECTION_ORDER) {
      const def = FIELD_REGISTRY[fieldKey]
      if (def?.askPatterns.some((p) => p.test(fragment))) {
        return fieldKey as keyof LabelData
      }
    }
  }
  return null
}

// Known country names and common "Made in X" patterns.
// Keyed on lowercase. Covers all major manufacturing countries + EU members.
const KNOWN_COUNTRIES = new Set([
  'china', 'china prc', 'prc', 'hong kong', 'hong kong sar', 'taiwan', 'taiwan roc',
  'japan', 'south korea', 'korea', 'vietnam', 'thailand', 'indonesia', 'india',
  'bangladesh', 'pakistan', 'malaysia', 'philippines', 'cambodia', 'myanmar',
  'sri lanka', 'turkey', 'mexico', 'brazil', 'colombia', 'argentina', 'peru',
  'united states', 'usa', 'us', 'united kingdom', 'uk', 'gb', 'great britain',
  'canada', 'australia', 'new zealand',
  'germany', 'france', 'italy', 'spain', 'netherlands', 'holland', 'belgium',
  'austria', 'switzerland', 'sweden', 'denmark', 'norway', 'finland', 'poland',
  'portugal', 'czechia', 'czech republic', 'hungary', 'romania', 'bulgaria',
  'croatia', 'slovakia', 'slovenia', 'estonia', 'latvia', 'lithuania',
  'luxembourg', 'ireland', 'greece', 'malta', 'cyprus',
  'israel', 'uae', 'united arab emirates', 'saudi arabia', 'egypt',
  'south africa', 'nigeria', 'kenya',
])

/**
 * Returns true if the value is clearly a country name rather than a street address.
 * Uses an explicit country list for precision + "Made in X" prefix detection.
 * Falls back to a narrow heuristic only if the value is very short and simple.
 */
function looksLikeCountry(value: string): boolean {
  const v = value.trim()
  if (!v) return false

  // "Made in X" prefix → definitely country of origin
  const madeIn = v.replace(/^made\s+in\s+/i, '').trim()
  if (/^made\s+in\s+/i.test(v)) {
    return KNOWN_COUNTRIES.has(madeIn.toLowerCase()) || madeIn.length <= 30
  }

  // Check against known country list first (most reliable)
  if (KNOWN_COUNTRIES.has(v.toLowerCase())) return true

  // Hard disqualifiers: any digit, comma (multi-part address), or street keyword
  if (/\d/.test(v)) return false
  if (v.includes(',')) return false
  if (/\b(street|st\b|avenue|ave\b|road|rd\b|boulevard|blvd|lane|ln\b|drive|dr\b|floor|suite|unit|building|bldg|weg|str\.|straße|strasse|laan|gata|väg|via\s|rue\s|platz|plein|allee|allée)\b/i.test(v)) return false

  // Only accept as country if very short (≤ 20 chars, 1-2 words)
  // This prevents city names like "Berlin" or "Amsterdam" from being treated as countries
  const wordCount = v.split(/\s+/).filter(Boolean).length
  return v.length <= 20 && wordCount <= 2
}

export function applyFieldAnswer(
  field: keyof LabelData,
  userText: string,
  currentData: LabelData,
): Partial<LabelData> {
  const def = FIELD_REGISTRY[field]
  if (!def) return {}

  const value = userText.trim()
  if (!value) return {}

  // Check skip phrases first
  if (def.skipPhrases && def.skipAction && def.skipPhrases.test(value)) {
    return def.skipAction
  }

  // Guard: if the detected field is manufacturerAddress but the answer looks like a
  // country name (e.g. "China", "Made in China"), redirect to countryOfOrigin.
  // This prevents short country answers from being stored in the address field when
  // the AI asks a combined question like "What is the manufacturer's address/country?"
  if (field === 'manufacturerAddress' && looksLikeCountry(value)) {
    const countryDef = FIELD_REGISTRY['countryOfOrigin']
    const countryUpdates: Partial<LabelData> = { countryOfOrigin: value }
    if (countryDef?.crossPopulate) {
      Object.assign(countryUpdates, countryDef.crossPopulate(value, currentData))
    }
    return countryUpdates
  }

  const updates: Partial<LabelData> = { [field]: value }

  // Cross-populate related fields
  if (def.crossPopulate) {
    Object.assign(updates, def.crossPopulate(value, currentData))
  }

  return updates
}

/**
 * Parse AI [FIELD:name=value] markers from an AI response.
 * Handles aliases, skip detection for importer fields, special value normalization.
 * Returns Partial<LabelData> — caller decides what to apply.
 */
export function parseAIMarkers(
  aiResponse: string,
  currentData: LabelData,
): Partial<LabelData> {
  const updates: Partial<LabelData> = {}
  const fieldPattern = /\[FIELD:(\w+)=([^\]]+)\]/g
  let match: RegExpExecArray | null

  // Build reverse alias map: alias → canonical field key
  const aliasMap: Record<string, string> = {}
  for (const [key, def] of Object.entries(FIELD_REGISTRY)) {
    for (const alias of def.aiMarkerAliases ?? []) {
      aliasMap[alias] = key
    }
  }

  // Detect if AI is asking about importer in this response (blocks premature importer tags)
  const isAskingAboutImporter = /importer[^.!]*\?/i.test(aiResponse)

  while ((match = fieldPattern.exec(aiResponse)) !== null) {
    const [, rawField, rawValue] = match
    const fieldKey = aliasMap[rawField] ?? rawField
    const trimmed = rawValue.trim()
    const def = FIELD_REGISTRY[fieldKey]

    // Skip unknown fields
    if (!def && !isKnownSpecialField(fieldKey)) continue

    // Block importer fields when the AI is still asking about them
    if (['euImporterName', 'euImporterAddress', 'euImporterSkipped'].includes(fieldKey)) {
      if (isAskingAboutImporter) continue
    }

    // Special handling per field
    switch (fieldKey) {
      case 'safetyWarning': {
        const skip = ['none', 'n/a', 'na', '-', '—', '']
        if (!skip.includes(trimmed.toLowerCase())) {
          updates.safetyWarning = trimmed
        }
        break
      }
      case 'euImporterSkipped':
        updates.euImporterSkipped = trimmed === 'true'
        break
      case 'containsBattery':
        updates.containsBattery = trimmed === 'true'
        break
      case 'showRecycling':
        updates.showRecycling = trimmed === 'true'
        break
      case 'targetMarkets': {
        const markets = trimmed.split(',').map((m) => m.trim()).filter(Boolean) as LabelData['targetMarkets']
        if (markets.length > 0) {
          updates.targetMarkets = [...new Set([...currentData.targetMarkets, ...markets])]
        }
        break
      }
      case 'frPackagingTemplate': {
        // Normalize: split into material keywords, sort them, join — order-independent.
        const keywords = trimmed.toLowerCase()
          .replace(/\b(and|&|plus|\+|with|bag|box)\b/g, ' ')
          .split(/[\s_,]+/)
          .map((w) => w.trim())
          .filter((w) => w === 'paper' || w === 'plastic')
        const unique = [...new Set(keywords)].sort()
        let resolved: LabelData['frPackagingTemplate'] | undefined
        if (unique.length === 1 && unique[0] === 'paper') resolved = 'paper'
        else if (unique.length === 1 && unique[0] === 'plastic') resolved = 'plastic'
        else if (unique.includes('paper') && unique.includes('plastic')) resolved = 'paper_plastic'
        if (resolved) updates.frPackagingTemplate = resolved
        break
      }
      default: {
        // Generic string field — set directly if it's a known string field
        if (def && isStringField(fieldKey)) {
          (updates as Record<string, unknown>)[fieldKey] = trimmed
          // Also fire cross-population (e.g. countryOfOrigin → manufacturerCountry)
          if (def.crossPopulate) {
            const cross = def.crossPopulate(trimmed, { ...currentData, ...updates } as LabelData)
            Object.assign(updates, cross)
          }
        }
        break
      }
    }
  }

  return updates
}

/** Fields that are boolean/enum/array — not plain strings */
const NON_STRING_FIELDS = new Set([
  'containsBattery', 'showRecycling', 'containsSmallParts', 'ageRestrictionUnder3',
  'euImporterSkipped', 'radio', 'targetMarkets', 'frPackagingTemplate',
  'productType', 'labelMode', 'productCategory',
])

function isStringField(field: string): boolean {
  return !NON_STRING_FIELDS.has(field)
}

/** Fields handled in parseAIMarkers but not in FIELD_REGISTRY (booleans set during onboarding) */
function isKnownSpecialField(field: string): boolean {
  return ['containsBattery', 'showRecycling', 'containsSmallParts', 'ageRestrictionUnder3',
    'euImporterSkipped', 'radio', 'targetMarkets', 'frPackagingTemplate'].includes(field)
}

/**
 * Get the required fields for this label state, in collection order.
 * Used by doAIHandoff to build the neededFields list.
 */
export function getRequiredFields(data: LabelData): Array<{ key: string; def: FieldDefinition }> {
  return Object.entries(FIELD_REGISTRY)
    .filter(([, def]) => def.required(data))
    .sort((a, b) => a[1].order - b[1].order)
    .map(([key, def]) => ({ key, def }))
}

/**
 * Get fields that must NOT be asked for this label state.
 * Used by doAIHandoff to build the doNotAsk list.
 */
export function getDoNotAskFields(data: LabelData): Array<{ key: string; reason: string }> {
  const doNotAsk: Array<{ key: string; reason: string }> = []

  if (!shouldRequireUKRep(data)) {
    doNotAsk.push({
      key: 'ukRepName / ukRepAddress',
      reason: 'Product is NOT UKCA-regulated — general consumer products do not need a UK Responsible Person.',
    })
  }

  if (hasEUMarket(data) && isCERegulated(data)) {
    doNotAsk.push({
      key: 'euImporterName / euImporterAddress',
      reason: 'CE-regulated products (electronics, toys) only require an EU Authorised Representative, NOT an EU Importer.',
    })
  }

  return doNotAsk
}

/**
 * Build the system prompt section for field rationales.
 * Auto-generated from the registry — no more hand-written rationale strings in the prompt.
 */
export function buildFieldRationalesPrompt(): string {
  return Object.entries(FIELD_REGISTRY)
    .map(([key, def]) => `- ${key}: "${def.rationale}"`)
    .join('\n')
}

/**
 * Build the collection instructions section for fields that have them.
 * Injected into the handoff summary.
 */
export function buildCollectInstructionsPrompt(data: LabelData): string {
  const lines: string[] = []
  for (const { key, def } of getRequiredFields(data)) {
    if (def.collectInstructions) {
      lines.push(`[${key}]\n${def.collectInstructions}`)
    }
  }
  return lines.join('\n\n')
}
